import * as React from "react";
import { forwardRef } from "react";
import {
  Paper,
  FormGroup,
  FormControlLabel,
  Grid,
  Switch,
  Alert,
  AlertColor,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
} from "@mui/material";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";

import http from "../../http/axios";
import {
  fetchEnvs,
  fetchConf,
  fetchClients,
  fetchConfNames,
} from "../../http/api";
import { Env, ConfName, Client } from "../../store/features/envSlice";


const RENDER_STYLES = {
  variables: {
    light: {
      codeFoldGutterBackground: "#6F767E",
      codeFoldBackground: "#E2E4E5",
    },
  },
};


interface EnvSelectorProps {
  setQueryData: (q: Partial<QueryDataType>) => void;
  setShow: (s: ShowType) => void;
  label: string;
}

interface ConfSelectorProps {
  setQueryData: (q: Partial<QueryDataType>) => void;
  setShow: (s: ShowType) => void;
  label: string;
}

interface QueryDataType {
  env?: Env;
  clientenv?: Client;
  confName?: ConfName;
}

type ShowType = { open: boolean; message: string; type: AlertColor };


const EnvSelector = forwardRef<any, EnvSelectorProps>(
  ({ setQueryData, setShow, label }, ref) => {
    const [env, setEnv] = React.useState({} as Env);
    const [envlist, setEnvlist] = React.useState<Env[]>([]);

    React.useEffect(() => {
      fetchEnvs()
        .then((response) => {
          setEnvlist(response.data.results);
        })
        .catch((error) => {
          if (error.response && error.response.status === 400) {
            setShow({
              open: true,
              message: error.response.data.error_description,
              type: "error",
            });
          } else {
            setShow({ open: true, message: error.message, type: "error" });
          }
        });
    }, [setShow]);

    const handleChangeEnv = (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = envlist.find(
        (_env) => _env.env_code === event.target.value
      );
      if (selected) setEnv(selected);
    };

    React.useEffect(() => {
      setQueryData({ env });
    }, [env, setQueryData]);

    const handleRefresh = () => {
      setEnv({} as Env);
    };

    React.useImperativeHandle(ref, () => ({
      handleRefresh,
    }));

    return (
      <>
        <Typography variant="h5">
          Environment <span>{env.env_code ? `[${env.env_name}]` : label}</span>
        </Typography>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            id={`env-${label}`}
            select
            label="Select an environment"
            value={env.env_code || ""}
            onChange={handleChangeEnv}
            variant="filled"
            fullWidth
          >
            {envlist.map((option) => (
              <MenuItem key={option.id} value={option.env_code}>
                {option.env_name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </>
    );
  }
);


const ConfSelector = forwardRef<any, ConfSelectorProps>(
  ({ setQueryData, setShow, label }, ref) => {
    const [env, setEnv] = React.useState({} as Env);
    const [envlist, setEnvlist] = React.useState<Env[]>([]);
    const [clientenv, setClientenv] = React.useState({} as Client);
    const [clientenvList, setClientenvList] = React.useState<Client[]>([]);
    const [confName, setConfName] = React.useState({} as ConfName);
    const [confNameList, setConfNameList] = React.useState<ConfName[]>([]);

    React.useEffect(() => {
      fetchEnvs()
        .then((response) => {
          setEnvlist(response.data.results);
        })
        .catch((error) => {
          if (error.response && error.response.status === 400) {
            setShow({
              open: true,
              message: error.response.data.error_description,
              type: "error",
            });
          } else {
            setShow({ open: true, message: error.message, type: "error" });
          }
        });
    }, [setShow]);

    const clearConf = () => {
      setConfName({} as ConfName);
      setQueryData({});
    };

    const handleChangeEnv = (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = envlist.find(
        (_env) => _env.env_code === event.target.value
      );
      setConfNameList([]);
      if (selected) setEnv(selected);
    };

    const handleChangeClientEnv = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const client = clientenvList.find(
        (c) => c.id === Number(event.target.value)
      );
      setConfName({} as ConfName);
      if (client) setClientenv(client);
    };

    const handleChangeConfEnv = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const conf = confNameList.find((c) => c.name === event.target.value);
      if (conf) setConfName(conf);
    };

    React.useEffect(() => {
      if (env.id !== undefined) {
        fetchClients(env).then((response) => {
          setClientenvList(response.data);
          setClientenv({} as Client);
          clearConf();
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [env]);

    React.useEffect(() => {
      if (clientenv.id !== undefined) {
        fetchConfNames(env).then((response) => {
          setConfNameList(response.data);
        });
      }
    }, [clientenv, env]);

    React.useEffect(() => {
      if (confName.name !== undefined) {
        setQueryData({ env, clientenv, confName });
      }
    }, [env, clientenv, confName, setQueryData]);

    const handleRefresh = async () => {
      setEnv({} as Env);
      setClientenv({} as Client);
      setConfName({} as ConfName);

      setEnvlist([]);
      setClientenvList([]);
      setConfNameList([]);

      setQueryData({});

      try {
        const response = await fetchEnvs();
        setEnvlist(response.data.results);
      } catch (error: any) {
        if (error.response && error.response.status === 400) {
          setShow({
            open: true,
            message: error.response.data.error_description,
            type: "error",
          });
        } else {
          setShow({ open: true, message: error.message, type: "error" });
        }
      }
    };

    React.useImperativeHandle(ref, () => ({
      handleRefresh,
    }));

    return (
      <>
        <Typography variant="h5">
          Environment <span>{env.env_code ? `[${env.env_name}]` : label}</span>
        </Typography>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            id={`env-${label}`}
            select
            label="Select an environment"
            value={env.env_code || ""}
            onChange={handleChangeEnv}
            variant="filled"
            fullWidth
          >
            {envlist.map((option) => (
              <MenuItem key={option.id} value={option.env_code}>
                {option.env_name}
              </MenuItem>
            ))}
          </TextField>

          <br />
          {env.id && (
            <TextField
              id={`clientenv-${label}`}
              select
              label="Select client"
              value={clientenv.id || ""}
              onChange={handleChangeClientEnv}
              variant="filled"
              fullWidth
            >
              {clientenvList.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.fullname}
                </MenuItem>
              ))}
            </TextField>
          )}

          <div style={{ marginTop: 15 }} />
          {clientenv.id && (
            <TextField
              id={`confName-${label}`}
              select
              label="Select configuration name"
              value={confName.name || ""}
              onChange={handleChangeConfEnv}
              variant="filled"
              fullWidth
            >
              {confNameList.map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </>
    );
  }
);


export default function Confchecker() {
  const rightSelectorRef = React.useRef<any>(null);
  const leftSelectorRef = React.useRef<any>(null);

  const [leftCode, setLeftCode] = React.useState("");
  const [rightCode, setRightCode] = React.useState("");

  const [show, setShow] = React.useState<ShowType>({
    open: false,
    message: "",
    type: "success",
  });

  const [linked, setLinked] = React.useState(true);

  const [leftQueryData, setLeftQueryData] = React.useState<QueryDataType>({});
  const [rightQueryData, setRightQueryData] = React.useState<QueryDataType>({});

  const [pushOpen, setPushOpen] = React.useState(false);
  const [pushing, setPushing] = React.useState(false);

  const sourceReady =
    Boolean(leftQueryData?.env?.env_code) &&
    leftQueryData?.clientenv?.id !== undefined &&
    Boolean(leftQueryData?.confName?.name);

  const targetReady = Boolean(rightQueryData?.env?.env_code) &&
    (linked
      ? true
      : leftQueryData?.clientenv?.id !== undefined &&
        Boolean(leftQueryData?.confName?.name) &&
        rightQueryData?.clientenv?.id !== undefined &&
        Boolean(rightQueryData?.confName?.name));


  const confNamesMatch = linked
    ? true
    : Boolean(leftQueryData?.confName?.name) &&
      Boolean(rightQueryData?.confName?.name) &&
      leftQueryData?.confName?.name === rightQueryData?.confName?.name;

  const canPush = sourceReady && targetReady && confNamesMatch;

  const handleLinkedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLinked(event.target.checked);
    if (event.target.checked) {
      setRightQueryData((prev) => ({ env: prev.env }));
    }
  };

  const handleRefresh = () => {
    if (leftSelectorRef.current) leftSelectorRef.current.handleRefresh();
    if (rightSelectorRef.current) rightSelectorRef.current.handleRefresh();
    setLeftCode("");
    setRightCode("");
    setLeftQueryData({});
    setRightQueryData({});
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setShow((s) => ({ ...s, open: false }));
  };

  const _fetchConf = (data: QueryDataType, setCode: (s: string) => void) => {
    if (
      data.env &&
      data.clientenv &&
      data.clientenv.id !== undefined &&
      data.confName &&
      data.confName.name !== undefined
    ) {
      fetchConf(data.env, data.clientenv, data.confName)
        .then((response) => {
          setCode(JSON.stringify(response.data.data, undefined, 2));
        })
        .catch(() => {
          setShow({
            open: true,
            message: "Failed to load conf",
            type: "error",
          });
        });
    } else {
      setCode("");
    }
  };

  React.useEffect(() => {
    _fetchConf(leftQueryData, setLeftCode);
    if (linked) {
      setRightQueryData((data) => ({ ...data }));
    }
  }, [leftQueryData, linked]);

  React.useEffect(() => {
    const data = linked
      ? { ...leftQueryData, env: rightQueryData.env }
      : rightQueryData;
    _fetchConf(data, setRightCode);
  }, [rightQueryData, linked, leftQueryData]);

  /** -------------------- push logic -------------------- */
  const openPushDialog = () => setPushOpen(true);
  const closePushDialog = () => setPushOpen(false);

  const doPush = async () => {
    if (!canPush) return;
    setPushing(true);
    try {
      const sourceEnvCode = leftQueryData.env!.env_code;
      const targetEnvCode = rightQueryData.env!.env_code;

      const targetClientId = linked
        ? leftQueryData.clientenv!.id
        : rightQueryData.clientenv!.id;

      const targetConfName = linked
        ? leftQueryData.confName!.name
        : rightQueryData.confName!.name;

      const payload = {
        source_env: sourceEnvCode,
        target_env: targetEnvCode,
        client: targetClientId,
        conf_name: targetConfName,
      };

      const res = await http.post("/configurations/push/", payload, {
        timeout: 30000,
      });

      const msg =
        (res && (res.data?.message as string)) ||
        "Configuration copiée avec succès";
      setShow({ open: true, message: msg, type: "success" });

      const dataForRight = linked
        ? { ...leftQueryData, env: rightQueryData.env }
        : rightQueryData;
      _fetchConf(dataForRight, setRightCode);

    } catch (err: any) {
      const errMsg =
        err?.response?.data?.error || err?.message || "Erreur lors du push";
      setShow({
        open: true,
        message: `Push échoué : ${errMsg}`,
        type: "error",
      });
    } finally {
      setPushing(false);
      setPushOpen(false);
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <Typography variant="h4" className="bold">
            Configurations Compare
          </Typography>
          <Button onClick={handleRefresh} sx={{ ml: 1 }}>
            Refresh
          </Button>
        </Grid>

        <Grid item xs={12} md={12} lg={12}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            <Grid
              container
              alignContent="space-between"
              spacing={3}
              justifyItems="stretch"
            >
              <Grid item xs={12} md={6} lg={6}>
                <ConfSelector
                  setQueryData={setLeftQueryData}
                  setShow={setShow}
                  label="A"
                  ref={leftSelectorRef}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                {linked ? (
                  <EnvSelector
                    setQueryData={setRightQueryData}
                    setShow={setShow}
                    label="B"
                    ref={rightSelectorRef}
                  />
                ) : (
                  <ConfSelector
                    setQueryData={setRightQueryData}
                    setShow={setShow}
                    label="B"
                    ref={rightSelectorRef}
                  />
                )}

                <FormGroup sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch checked={linked} onChange={handleLinkedChange} />
                    }
                    label="linked"
                  />
                </FormGroup>

                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ mt: 2 }}
                  onClick={openPushDialog}
                  disabled={!canPush || pushing}
                >
                  {pushing ? "Pushing..." : "Push A → B"}
                </Button>

                <Dialog open={pushOpen} onClose={closePushDialog}>
                  <DialogTitle>Confirmer le push de configuration</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Cette action va copier la configuration suivante :
                    </DialogContentText>
                    <Box sx={{ mt: 2 }}>
                      <Typography>
                        - Source env : {leftQueryData?.env?.env_code} (
                        {leftQueryData?.env?.env_name})
                      </Typography>
                      <Typography>
                        - Cible env : {rightQueryData?.env?.env_code} (
                        {rightQueryData?.env?.env_name})
                      </Typography>
                      <Typography>
                        - Source client : {leftQueryData?.clientenv?.fullname}
                      </Typography>
                      <Typography>
                        - Cible client : {" "}
                        {linked
                            ? leftQueryData?.clientenv?.fullname
                            : rightQueryData?.clientenv?.fullname}
                      </Typography>
                      <Typography>
                        - Fichier :{" "}
                        {linked
                          ? leftQueryData?.confName?.name
                          : rightQueryData?.confName?.name}
                      </Typography>
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={closePushDialog} disabled={pushing}>
                      Annuler
                    </Button>
                    <Button
                      onClick={doPush}
                      disabled={!canPush || pushing}
                      variant="contained"
                      color="primary"
                    >
                      {pushing ? "Envoi..." : "Confirmer"}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>

              <Grid item xs={12}>
                <ReactDiffViewer
                  oldValue={leftCode}
                  newValue={rightCode}
                  splitView={true}
                  compareMethod={DiffMethod.WORDS}
                  styles={RENDER_STYLES}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={show.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={show.type}
          sx={{ width: "100%" }}
        >
          {show.message}
        </Alert>
      </Snackbar>
    </>
  );
}
